// src/encounter/discard-note-confirm-dialog.tsx
// Confirmation before discarding the encounter note. PRESENTATIONAL — composes the DS Modal + Button.
//
// Andrew, 2026-08-26, asked for Flutter's three note-footer buttons; "do as flutter" on the scope.
//
// FLUTTER SOURCE. `showDiscardNoteDialog` (`generated_notes_widget.dart:502-514`) calls the shared
// `confirmDialog` (`scrips_core/lib/widgets/general/alert_dialog.dart:7-125`) with:
//
//   title    S.discardDialogTitle  "Do you really want to discard the note?"
//   message  S.discardDialogMsg    "If you discard your progress on the note will be lost."
//   yesText  S.discard             "Discard"   -> onYes: bloc.discardEncounter() then pop
//   noText   S.keep                "Keep"
//
// Strings are verbatim from `lib/l10n/intl_en.arb:320-323` — including "If you discard your progress
// on the note will be lost.", which reads like a comma is missing. It is Flutter's copy; matching the
// source beats improving it, the same call the sign dialog made. Do not "fix" the grammar here
// without changing it in Flutter too, or the two apps will say different things.
//
// SHAPE FOLLOWS `sign-note-confirm-dialog.tsx`, which was built for full parity under PROD-2012 —
// see that file's header for the three properties of Flutter's `confirmDialog` that deliberately
// differ from this app's other dialogs. All three apply here as well:
//
//   1. BUTTON ORDER is `noText` first, then `yesText` (alert_dialog.dart:67-110). For THIS dialog
//      that is [Keep] [Discard] — the safe action leads and the destructive one sits second, which
//      happens to be the conventional ordering too. It is Flutter's ordering that decides it, not
//      the convention: `noText` is simply rendered first.
//   2. LEFT-ALIGNED — Flutter's button Row has no leading Spacer (:68).
//   3. NOT DISMISSIBLE — `barrierDismissible: false` (:23), so `onClose` is inert and the two
//      buttons are the only exits. Correct for a destructive action: a stray backdrop click must not
//      be able to resolve it either way.
//
// The destructive button is `variant="destructive"` where the sign dialog uses `primary`: Flutter colours
// this pill `redTextEE6464` in the footer, and discarding cannot be undone.
import { Modal, Button } from '@scripsteam/scrips-signal-ds';

export interface DiscardNoteConfirmDialogProps {
  open: boolean;
  /** The discard writes are in flight; keeps the user from double-firing them. */
  discarding?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const DISCARD_CONFIRM_TITLE = 'Do you really want to discard the note?';
export const DISCARD_CONFIRM_MESSAGE =
  'If you discard your progress on the note will be lost.';

export function DiscardNoteConfirmDialog({
  open,
  discarding = false,
  onConfirm,
  onCancel,
}: DiscardNoteConfirmDialogProps) {
  if (!open) return null;
  return (
    <Modal
      open
      // INERT on purpose — Flutter's `barrierDismissible: false`. A backdrop click or Escape must
      // not resolve a destructive choice; Keep exists for that.
      onClose={() => {}}
      title={DISCARD_CONFIRM_TITLE}
      size="sm"
    >
      <div className="font-sans">
        <p className="m-0 text-size-sm leading-prose text-text-primary">
          {DISCARD_CONFIRM_MESSAGE}
        </p>
      </div>

      {/* Flutter order + alignment: `noText` (Keep) first, then `yesText` (Discard), left-aligned. */}
      <div className="flex justify-start gap-2 mt-4">
        <Button variant="tertiary" size="sm" onClick={onCancel} disabled={discarding}>
          Keep
        </Button>
        <Button variant="destructive" size="sm" onClick={onConfirm} disabled={discarding}>
          {discarding ? 'Discarding…' : 'Discard'}
        </Button>
      </div>
    </Modal>
  );
}
